import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  TableHead,
  Grid,
  Typography,
  Box,
  Button,
  CircularProgress,
  Chip,
  Tooltip,
  Skeleton,
  Stack,
  Tabs,
  Tab,
  alpha,
  IconButton,
  Dialog,
  CardActionArea,
} from '@mui/material';
import { Icon } from '@iconify/react';
import moment from 'moment';
import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import SearchNotFound from 'src/components/SearchNotFound';
import { useQuery } from '@tanstack/react-query';
import { studentService } from 'src/services/student.service';
import { transactionService } from 'src/services/transaction.service';
import { loanService } from 'src/services/loan.service';
import { financialSimulationService } from 'src/services/financial-simulation.service';
import { studentBehaviourService } from 'src/services/student-behaviour.service';
import palette from 'src/theme/palette';
import LineChart from 'src/components/LineChart';
import DonutChart from 'src/components/DonutChart';
import { PaycheckStub } from 'src/pages/Admin/payroll/components/PaycheckStub';
import { formatTransactionItem } from 'src/utils/transactionFormat';
import { chargeNote, chargeTitle } from 'src/utils/chargeDisplay';
import { useAuth } from 'src/context/auth/AuthContext';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';

// Metric Card Component matching high-density educator design language
interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  isLoading?: boolean;
  onClick?: () => void;
}

function MetricCard({ title, value, subtitle, icon, iconBg, iconColor, isLoading, onClick }: MetricCardProps) {
  return (
    <Card
      elevation={0}
      component={onClick ? CardActionArea : 'div'}
      onClick={onClick}
      sx={{
        p: 1.75,
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        },
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}
        >
          {title}
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width={60} height={28} />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', my: 0.25, fontSize: '1.25rem', letterSpacing: '-0.01em' }}>
            {value}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
          {subtitle}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.75,
          bgcolor: iconBg,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon icon={icon} width={20} height={20} />
      </Box>
    </Card>
  );
}

function getStatusBadge(status?: string | null) {
  const normalized = status?.toLowerCase() || '';
  if (normalized.includes('approv') || normalized.includes('complet') || normalized.includes('highlight') || normalized.includes('active')) {
    return {
      label: status || 'Approved',
      color: '#059669',
      bgcolor: 'rgba(16, 185, 129, 0.12)',
      icon: 'solar:check-circle-bold-duotone',
    };
  }
  if (normalized.includes('pend') || normalized.includes('progress') || normalized.includes('review')) {
    return {
      label: status || 'Pending',
      color: '#D97706',
      bgcolor: 'rgba(245, 158, 11, 0.12)',
      icon: 'solar:hourglass-bold-duotone',
    };
  }
  if (normalized.includes('deni') || normalized.includes('reject') || normalized.includes('overdue') || normalized.includes('challenge')) {
    return {
      label: status || 'Declined',
      color: '#DC2626',
      bgcolor: 'rgba(239, 68, 68, 0.12)',
      icon: 'solar:close-circle-bold-duotone',
    };
  }
  return {
    label: status || 'Standard',
    color: '#2563EB',
    bgcolor: 'rgba(37, 99, 235, 0.12)',
    icon: 'solar:info-circle-bold-duotone',
  };
}

export default function StudentDashboard(): React.JSX.Element {
  const navigate = useNavigate();
  const { user, getBaseRoute } = useAuth();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedPaycheck, setSelectedPaycheck] = useState<{
    snapshot: any;
    amount: number;
    createdAt?: string;
  } | null>(null);

  useHeaderBreadcrumbs({
    breadcrumbs: [
      { label: 'Student Dashboard' },
    ],
  });

  // Fetch Student ID from User ID
  const { data: studentId, isLoading: isStudentIdLoading } = useQuery({
    queryKey: ['studentIdByUserId', user?.id],
    queryFn: () => studentService.getStudentByUserId(user?.id as string),
    enabled: !!user?.id,
  });

  // Financial Summary (Wallet Balance, Total Credits, Total Debits)
  const { data: financialSummary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['student-financial-summary', studentId],
    queryFn: () => studentService.getStudentFinancialSummary(studentId as string),
    enabled: !!studentId,
  });

  // Student Behavior Count & Metrics
  const { data: behaviorCount, isLoading: isBehaviorLoading } = useQuery({
    queryKey: ['student-behavior-count', studentId],
    queryFn: () => studentService.getStudentBehaviorCount(studentId as string),
    enabled: !!studentId,
  });

  // Street Cred History Data
  const { data: streetCredData, isLoading: isStreetCredLoading } = useQuery({
    queryKey: ['street-cred', studentId],
    queryFn: () => studentBehaviourService.getStudentBehaviours({ studentId: studentId as string }),
    enabled: !!studentId,
  });

  // Monthly Balance Data for LineChart
  const { data: monthlyBalance, isLoading: isMonthlyLoading } = useQuery({
    queryKey: ['student-monthly-balance', studentId],
    queryFn: () => studentService.getMonthlyBalance(studentId as string),
    enabled: !!studentId,
  });

  // Transactions Data (Approved & Completed)
  const { data: transactionsData, isLoading: isTransactionsLoading } = useQuery({
    queryKey: ['student-transactions', studentId],
    queryFn: () => transactionService.getTransactions(1, 10, { studentId: studentId as string }),
    enabled: !!studentId,
  });

  // Pending Transactions
  const { data: pendingTransactionsData, isLoading: isPendingLoading } = useQuery({
    queryKey: ['student-pending-transactions', studentId],
    queryFn: () => transactionService.getTransactions(1, 10, { studentId: studentId as string, status: 'Pending' }),
    enabled: !!studentId,
  });

  // Loan Requests Data
  const { data: loanRequests, isLoading: isLoansLoading } = useQuery({
    queryKey: ['student-loans', studentId],
    queryFn: () => loanService.getLoans({ studentId: studentId as string }),
    enabled: !!studentId,
  });

  // Financial Simulations Data
  const { data: financialSimulations, isLoading: isSimulationsLoading } = useQuery({
    queryKey: ['student-financial-simulations', studentId],
    queryFn: () => financialSimulationService.getChallenges({ page: 1, limit: 10 }),
    enabled: !!studentId,
  });

  // Calculated Metrics
  const currentBalance = Number(financialSummary?.currentBalance ?? 0);
  const totalCredit = Number(financialSummary?.totalCredit ?? 0);
  const totalDebit = Number(financialSummary?.totalDebit ?? 0);
  const positiveBehaviors = behaviorCount?.positive ?? 0;
  const negativeBehaviors = behaviorCount?.negative ?? 0;
  const netCredPoints = positiveBehaviors - negativeBehaviors;
  const pendingCount = pendingTransactionsData?.total ?? 0;

  // Chart data formatting
  const lineChartData = useMemo(() => {
    if (!monthlyBalance || monthlyBalance.length === 0) return [];
    return monthlyBalance.map((item) => ({
      name: moment(item.month).format('MMM'),
      value: item.balance,
    }));
  }, [monthlyBalance]);

  const donutChartData = useMemo(() => {
    return [positiveBehaviors, negativeBehaviors];
  }, [positiveBehaviors, negativeBehaviors]);

  const recentTransactions = transactionsData?.data || [];
  const pendingTransactionsList = pendingTransactionsData?.data || [];
  const loansList = loanRequests?.data || [];
  const streetCredList = streetCredData?.data || [];
  const simulationsList = financialSimulations?.data || [];

  const isGlobalLoading = isStudentIdLoading || isSummaryLoading;

  return (
    <Page title="Student Dashboard | Gold Minds Portal">
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        {/* Header Welcome Ribbon */}
        <Box sx={{ mb: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'rgba(37, 99, 235, 0.12)',
                  color: '#2563EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon icon="solar:user-bold-duotone" width={24} height={24} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em', fontSize: '1.375rem' }}>
                  Welcome back, {user?.displayName || 'Scholar'}!
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                  Track your virtual dollar growth, S.T.R.E.E.T Cred, and daily classroom activity.
                </Typography>
              </Box>
            </Stack>

            {/* Quick Action Navigation Buttons */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon icon="solar:card-2-bold-duotone" width={16} />}
                onClick={() => navigate(getBaseRoute('/street-cents'))}
                sx={{
                  height: 36,
                  borderRadius: 1.5,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
                }}
              >
                View Ledger
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon icon="solar:wallet-money-bold-duotone" width={16} />}
                onClick={() => navigate(getBaseRoute('/financial-tools'))}
                sx={{
                  height: 36,
                  borderRadius: 1.5,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
                }}
              >
                Auto Rules & Savings
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon icon="solar:hand-money-bold-duotone" width={16} />}
                onClick={() => navigate(getBaseRoute('/loan-requests'))}
                sx={{
                  height: 36,
                  borderRadius: 1.5,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
                }}
              >
                Request Loan
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* 5 High-Density KPI Metric Cards */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Wallet Balance"
              onClick={() => navigate(getBaseRoute('/street-cents'))}
              value={`$D ${currentBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle="Available to spend"
              icon="solar:wallet-2-bold-duotone"
              iconBg="rgba(16, 185, 129, 0.12)"
              iconColor="#059669"
              isLoading={isGlobalLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Total Earned"
              onClick={() => navigate(getBaseRoute('/street-cents'))}
              value={`+$D ${totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle="Credits & allowances"
              icon="solar:card-recive-bold-duotone"
              iconBg="rgba(37, 99, 235, 0.12)"
              iconColor="#2563EB"
              isLoading={isSummaryLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Total Spent"
              onClick={() => navigate(getBaseRoute('/street-cents'))}
              value={`-$D ${totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle="Debits & purchases"
              icon="solar:card-send-bold-duotone"
              iconBg="rgba(239, 68, 68, 0.12)"
              iconColor="#DC2626"
              isLoading={isSummaryLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="S.T.R.E.E.T Cred"
              onClick={() => navigate(getBaseRoute('/street-cred'))}
              value={`${netCredPoints >= 0 ? '+' : ''}${netCredPoints} pts`}
              subtitle="Net character merits"
              icon="solar:star-bold-duotone"
              iconBg="rgba(245, 158, 11, 0.12)"
              iconColor="#D97706"
              isLoading={isBehaviorLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Bills to Pay"
              onClick={() => navigate(getBaseRoute('/bills'))}
              value={pendingCount.toLocaleString()}
              subtitle={pendingCount > 0 ? 'Tap to pay now' : 'All caught up'}
              icon="solar:hourglass-bold-duotone"
              iconBg="rgba(245, 158, 11, 0.12)"
              iconColor="#D97706"
              isLoading={isPendingLoading}
            />
          </Grid>
        </Grid>

        {/* Interactive Charts Section */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {/* Monthly Balance Progression */}
          <Grid item xs={12} md={8}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                height: '100%',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                    Monthly Balance Trend
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    Account balance history across the academic term
                  </Typography>
                </Box>
                <Chip
                  icon={<Icon icon="solar:chart-2-bold-duotone" width={14} />}
                  label="Real-time"
                  size="small"
                  sx={{ height: 24, fontSize: '0.6875rem', fontWeight: 600, bgcolor: 'rgba(37, 99, 235, 0.08)', color: '#2563EB' }}
                />
              </Stack>
              {isMonthlyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : lineChartData.length > 0 ? (
                <LineChart label="S.T.R.E.E.T Cents Balance" data={lineChartData} />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 220 }}>
                  <Icon icon="solar:chart-square-linear" width={40} style={{ color: '#94A3B8', marginBottom: 8 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    No historical monthly balance recorded yet.
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>

          {/* S.T.R.E.E.T Cred Distribution */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                    S.T.R.E.E.T Cred Points
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    Character merits vs challenges
                  </Typography>
                </Box>
                <Chip
                  icon={<Icon icon="solar:star-bold-duotone" width={14} />}
                  label={`${positiveBehaviors + negativeBehaviors} logged`}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    bgcolor: positiveBehaviors + negativeBehaviors > 0 ? 'rgba(245, 158, 11, 0.08)' : 'grey.100',
                    color: positiveBehaviors + negativeBehaviors > 0 ? '#D97706' : 'text.secondary',
                  }}
                />
              </Stack>
              {isBehaviorLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <Box sx={{ width: '100%' }}>
                  <DonutChart
                    data={donutChartData}
                    labels={['Highlights (Merits)', 'Challenges (Demerits)']}
                    colors={[palette.primary.main, palette.error.main]}
                    height={170}
                  />
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* Activity & Workspaces Tabs Section */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          {/* Segmented Pill Tabs Navigation */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.neutral' }}>
            <Tabs
              value={activeTab}
              onChange={(_e, val) => setActiveTab(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTab-root': {
                  minHeight: 36,
                  py: 0.5,
                  px: 1.75,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  transition: 'all 0.15s ease',
                  '&.Mui-selected': {
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    fontWeight: 700,
                  },
                },
              }}
            >
              <Tab
                icon={<Icon icon="solar:card-2-bold" width={16} />}
                iconPosition="start"
                label={
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>Recent Transactions</span>
                    <Chip
                      label={recentTransactions.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        bgcolor: activeTab === 0 ? 'primary.main' : 'action.selected',
                        color: activeTab === 0 ? 'primary.contrastText' : 'text.secondary',
                      }}
                    />
                  </Stack>
                }
              />
              <Tab
                icon={<Icon icon="solar:hourglass-bold-duotone" width={16} />}
                iconPosition="start"
                label={
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>Pending Requests</span>
                    <Chip
                      label={pendingCount}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        bgcolor: activeTab === 1 ? 'warning.main' : 'action.selected',
                        color: activeTab === 1 ? 'warning.contrastText' : 'text.secondary',
                      }}
                    />
                  </Stack>
                }
              />
              <Tab
                icon={<Icon icon="solar:star-rainbow-bold-duotone" width={16} />}
                iconPosition="start"
                label={
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>S.T.R.E.E.T Cred Log</span>
                    <Chip
                      label={streetCredList.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        bgcolor: activeTab === 2 ? 'primary.main' : 'action.selected',
                        color: activeTab === 2 ? 'primary.contrastText' : 'text.secondary',
                      }}
                    />
                  </Stack>
                }
              />
              <Tab
                icon={<Icon icon="solar:hand-money-bold-duotone" width={16} />}
                iconPosition="start"
                label={
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>Loan Obligations</span>
                    <Chip
                      label={loansList.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        bgcolor: activeTab === 3 ? 'primary.main' : 'action.selected',
                        color: activeTab === 3 ? 'primary.contrastText' : 'text.secondary',
                      }}
                    />
                  </Stack>
                }
              />
              <Tab
                icon={<Icon icon="solar:gamepad-bold-duotone" width={16} />}
                iconPosition="start"
                label={
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>Whammies & Challenges</span>
                    <Chip
                      label={simulationsList.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        bgcolor: activeTab === 4 ? 'primary.main' : 'action.selected',
                        color: activeTab === 4 ? 'primary.contrastText' : 'text.secondary',
                      }}
                    />
                  </Stack>
                }
              />
            </Tabs>
          </Box>

          {/* Tab 0: Recent Transactions Table */}
          {activeTab === 0 && (
            <Scrollbar>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Category & Details
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Amount
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Balance After
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Date
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isTransactionsLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={28} />
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                            Loading recent transactions...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : recentTransactions.length > 0 ? (
                      recentTransactions.map((row: any) => {
                        const txMeta = formatTransactionItem(row);
                        const balanceAfter = Number(row?.balanceafter ?? 0);
                        const statusMeta = getStatusBadge(row?.status);
                        const viewTransactionPath = getBaseRoute(`/street-cents/${row.id}`);

                        const handleRowClick = () => {
                          if (txMeta.isPaycheck && txMeta.payrollSnapshot) {
                            setSelectedPaycheck({
                              snapshot: txMeta.payrollSnapshot,
                              amount: Number(row?.amount || 0),
                              createdAt: row?.createdat,
                            });
                          } else {
                            navigate(viewTransactionPath);
                          }
                        };

                        return (
                          <TableRow
                            key={row.id}
                            hover
                            sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                            onClick={handleRowClick}
                          >
                            <TableCell sx={{ py: 1.25 }}>
                              <Stack direction="row" alignItems="center" spacing={1.25}>
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: txMeta.iconBg,
                                    color: txMeta.iconColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Icon icon={txMeta.icon} width={18} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem', lineHeight: 1.3 }}
                                  >
                                    {txMeta.title}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', lineHeight: 1.3 }}
                                  >
                                    {txMeta.subtitle}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Chip
                                icon={
                                  <Icon
                                    icon={txMeta.isPaycheck ? 'solar:bill-check-bold-duotone' : txMeta.isCredit ? 'solar:card-recive-bold-duotone' : 'solar:card-send-bold-duotone'}
                                    width={13}
                                    color={txMeta.iconColor}
                                  />
                                }
                                label={txMeta.amountText}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '0.8125rem',
                                  fontWeight: 700,
                                  borderRadius: 1.25,
                                  bgcolor: txMeta.isCredit ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                  color: txMeta.isCredit ? '#059669' : '#DC2626',
                                  border: '1px solid',
                                  borderColor: txMeta.isCredit ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                                ${balanceAfter.toFixed(2)}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Chip
                                icon={<Icon icon={statusMeta.icon} width={13} />}
                                label={statusMeta.label}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  color: statusMeta.color,
                                  bgcolor: statusMeta.bgcolor,
                                  border: `1px solid ${statusMeta.color}22`,
                                  '& .MuiChip-icon': { color: `${statusMeta.color} !important`, ml: '4px' },
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.8125rem' }}>
                                {row?.createdat ? moment(row.createdat).format('MMM D, YYYY') : '--'}
                              </Typography>
                            </TableCell>

                            <TableCell align="right" sx={{ py: 1.75 }}>
                              <Tooltip title={txMeta.isPaycheck ? 'View Paycheck Stub' : 'View Transaction Details'}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRowClick();
                                  }}
                                  sx={{
                                    p: 0.75,
                                    bgcolor: 'grey.100',
                                    color: 'text.secondary',
                                    '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' },
                                  }}
                                >
                                  <Icon icon={txMeta.isPaycheck ? 'solar:document-text-bold-duotone' : 'solar:eye-bold-duotone'} width={18} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 4 }}>
                          <SearchNotFound message="No recent transactions found" />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  endIcon={<Icon icon="solar:arrow-right-linear" width={16} />}
                  onClick={() => navigate(getBaseRoute('/street-cents'))}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
                >
                  View Full S.T.R.E.E.T Cents Ledger
                </Button>
              </Box>
            </Scrollbar>
          )}

          {/* Tab 1: Pending Approvals Table */}
          {activeTab === 1 && (
            <Scrollbar>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Requested Item / Reason
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Amount
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Date Requested
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Status
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isPendingLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : pendingTransactionsList.length > 0 ? (
                      pendingTransactionsList.map((row: any) => {
                        const statusMeta = getStatusBadge(row?.status || 'Pending');
                        const isCredit = row?.transaction_type?.type === 'credit';
                        const amount = Number(row?.amount || 0);

                        return (
                          <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                                {chargeTitle(row)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {chargeNote(row) || 'Waiting for you to pay'}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: isCredit ? 'success.main' : 'error.main',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {isCredit ? '+' : '-'}${amount.toFixed(2)}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.8125rem' }}>
                                {row?.createdat ? moment(row.createdat).format('MMM D, YYYY') : '--'}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Chip
                                icon={<Icon icon={statusMeta.icon} width={13} />}
                                label={statusMeta.label}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  color: statusMeta.color,
                                  bgcolor: statusMeta.bgcolor,
                                  border: `1px solid ${statusMeta.color}22`,
                                  '& .MuiChip-icon': { color: `${statusMeta.color} !important`, ml: '4px' },
                                }}
                              />
                            </TableCell>

                            <TableCell align="right" sx={{ py: 1.75 }}>
                              {isCredit ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => navigate(getBaseRoute(`/street-cents/${row.id}`))}
                                  sx={{ height: 28, fontSize: '0.75rem', borderRadius: 1.25, textTransform: 'none' }}
                                >
                                  View
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => navigate(getBaseRoute('/bills'))}
                                  sx={{ height: 28, fontSize: '0.75rem', borderRadius: 1.25, textTransform: 'none', boxShadow: 'none' }}
                                >
                                  Pay Now
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 4 }}>
                          <SearchNotFound message="No pending transaction requests found" />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}

          {/* Tab 2: S.T.R.E.E.T Cred Log Table */}
          {activeTab === 2 && (
            <Scrollbar>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Character Recognition
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Points
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Classification
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Date Logged
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isStreetCredLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : streetCredList.length > 0 ? (
                      streetCredList.map((row: any) => {
                        const isHighlight = row?.behaviour_types?.type !== 'challenge';
                        const points = Number(row?.behaviour_types?.points || 0);

                        return (
                          <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ py: 1.75 }}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: isHighlight ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                                    color: isHighlight ? '#059669' : '#DC2626',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Icon
                                    icon={isHighlight ? 'solar:medal-ribbon-star-bold-duotone' : 'solar:danger-triangle-bold-duotone'}
                                    width={18}
                                  />
                                </Box>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                                    {row?.behaviour_types?.title || 'Behavior Log'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                    {row?.description || (isHighlight ? 'Positive merit recognition' : 'Growth & improvement challenge')}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: isHighlight ? 'success.main' : 'error.main',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {isHighlight ? '+' : '-'}{points} pts
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Chip
                                icon={<Icon icon={isHighlight ? 'solar:check-circle-bold-duotone' : 'solar:shield-warning-bold-duotone'} width={13} />}
                                label={isHighlight ? 'Highlight' : 'Challenge'}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  color: isHighlight ? '#059669' : '#DC2626',
                                  bgcolor: isHighlight ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                                  border: `1px solid ${isHighlight ? '#05966922' : '#DC262622'}`,
                                  '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.8125rem' }}>
                                {row?.createdAt ? moment(row.createdAt).format('MMM D, YYYY') : '--'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 4 }}>
                          <SearchNotFound message="No S.T.R.E.E.T Cred records found" />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  endIcon={<Icon icon="solar:arrow-right-linear" width={16} />}
                  onClick={() => navigate(getBaseRoute('/street-cred'))}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
                >
                  View Full S.T.R.E.E.T Cred Hub
                </Button>
              </Box>
            </Scrollbar>
          )}

          {/* Tab 3: Loan Obligations Table */}
          {activeTab === 3 && (
            <Scrollbar>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Loan Purpose
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Principal Amount
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Repayment Term
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Monthly Payment
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoansLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : loansList.length > 0 ? (
                      loansList.map((row: any) => {
                        const statusMeta = getStatusBadge(row?.status);
                        const amount = Number(row?.amount || 0);
                        const monthlyPayment = Number(row?.monthly_payment || 0);

                        return (
                          <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                                {row?.reason || 'Educational Microloan'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                Applied on {row?.createdat ? moment(row.createdat).format('MMM D, YYYY') : '--'}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                                ${amount.toFixed(2)}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.8125rem' }}>
                                {row?.term_months} {Number(row?.term_months) === 1 ? 'month' : 'months'}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem' }}>
                                ${monthlyPayment.toFixed(2)}/mo
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Chip
                                icon={<Icon icon={statusMeta.icon} width={13} />}
                                label={statusMeta.label}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  color: statusMeta.color,
                                  bgcolor: statusMeta.bgcolor,
                                  border: `1px solid ${statusMeta.color}22`,
                                  '& .MuiChip-icon': { color: `${statusMeta.color} !important`, ml: '4px' },
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 4 }}>
                          <SearchNotFound message="No loan requests or obligations found" />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  startIcon={<Icon icon="solar:hand-money-bold-duotone" width={16} />}
                  onClick={() => navigate(getBaseRoute('/loan-requests'))}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
                >
                  Manage Loan Requests
                </Button>
              </Box>
            </Scrollbar>
          )}

          {/* Tab 4: Whammies & Financial Simulations Table */}
          {activeTab === 4 && (
            <Scrollbar>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Scenario & Simulation
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Duration
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Target Amount
                      </TableCell>
                      <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Status
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isSimulationsLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : simulationsList.length > 0 ? (
                      simulationsList.map((row: any) => {
                        const statusMeta = getStatusBadge(row?.status);
                        const viewSimPath = getBaseRoute(`/view-financial-simulations/${row.id}`);

                        return (
                          <TableRow
                            key={row.id}
                            hover
                            sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                            onClick={() => navigate(viewSimPath)}
                          >
                            <TableCell sx={{ py: 1.75 }}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: 'rgba(124, 58, 237, 0.12)',
                                    color: '#7C3AED',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Icon icon="solar:gamepad-bold-duotone" width={18} />
                                </Box>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                                    {row?.scenario?.title || 'Financial Challenge'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                    Interactive real-world scenario
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.8125rem' }}>
                                {row?.duration} {typeof row?.duration === 'number' ? 'months' : ''}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                                ${Number(row?.amount || 0).toFixed(2)}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.75 }}>
                              <Chip
                                icon={<Icon icon={statusMeta.icon} width={13} />}
                                label={statusMeta.label}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  color: statusMeta.color,
                                  bgcolor: statusMeta.bgcolor,
                                  border: `1px solid ${statusMeta.color}22`,
                                  '& .MuiChip-icon': { color: `${statusMeta.color} !important`, ml: '4px' },
                                }}
                              />
                            </TableCell>

                            <TableCell align="right" sx={{ py: 1.75 }}>
                              <Tooltip title="View Simulation Details">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(viewSimPath);
                                  }}
                                  sx={{
                                    p: 0.75,
                                    bgcolor: 'grey.100',
                                    color: 'text.secondary',
                                    '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' },
                                  }}
                                >
                                  <Icon icon="solar:eye-bold-duotone" width={18} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 4 }}>
                          <SearchNotFound message="No assigned financial simulations found" />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  endIcon={<Icon icon="solar:arrow-right-linear" width={16} />}
                  onClick={() => navigate(getBaseRoute('/financial-simulations'))}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
                >
                  Explore All Challenges & Whammies
                </Button>
              </Box>
            </Scrollbar>
          )}
        </Card>

        {/* Paycheck Stub Detail Modal */}
        <Dialog
          open={!!selectedPaycheck}
          onClose={() => setSelectedPaycheck(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          {selectedPaycheck && (
            <PaycheckStub
              snapshot={selectedPaycheck.snapshot}
              studentName={user?.displayName || ''}
              transactionAmount={selectedPaycheck.amount}
              createdAt={selectedPaycheck.createdAt}
              onClose={() => setSelectedPaycheck(null)}
            />
          )}
        </Dialog>
      </Container>
    </Page>
  );
}
